import { useCallback, useRef, useState } from 'react'

/**
 * Per-fieldPath in-flight guard used by override-driven chip rows.
 *
 * The pattern: rapid taps on the same chip would otherwise fire parallel
 * PUT + GET round-trips. Whichever GET resolves last wins and silently reverts
 * the latest user intent. Wrapping each override mutation in `runWithGuard`
 * drops further taps on the same fieldPath while a write is in flight.
 *
 * Extracted from `BrandVisualSection` so `BrandVoiceSection` (and future
 * brand-learning sections) can reuse it without copy-paste.
 */
export interface InflightController {
  /** True iff a write keyed by `fieldPath` is still in flight. */
  has(fieldPath: string): boolean
  /**
   * Wrap an async operation in the guard. If a write keyed by `fieldPath` is
   * already in flight, the wrapped op is dropped silently — the originating
   * write will resolve and trigger the refetch on its own.
   */
  run(fieldPath: string, op: () => Promise<void>): Promise<void>
}

export function useInflightSet(): InflightController {
  // The ref is the source of truth so two synchronous `run()` calls in the
  // same render frame both observe the latest membership. The state Set is
  // only kept so `has()` returns a referentially-stable value across renders
  // and re-renders fire when membership changes (for disabled chip states).
  const inflightRef = useRef<Set<string>>(new Set())
  const [inflight, setInflight] = useState<Set<string>>(inflightRef.current)

  const has = useCallback((fieldPath: string) => inflight.has(fieldPath), [inflight])

  const run = useCallback(async (fieldPath: string, op: () => Promise<void>) => {
    // Read off the ref, not the state Set — the state Set is stale within
    // a single render frame, so concurrent calls would both proceed.
    if (inflightRef.current.has(fieldPath)) return
    inflightRef.current.add(fieldPath)
    setInflight(new Set(inflightRef.current))
    try {
      await op()
    } finally {
      inflightRef.current.delete(fieldPath)
      setInflight(new Set(inflightRef.current))
    }
  }, [])

  return { has, run }
}
